import Keycloak from 'keycloak-js';
import fetchMemberOrganizations from '@/services/memberOrganizations';
import {
  clearKeycloakCallbackHash,
  clearKeycloakCallbackStorage,
  getKeycloakRedirectUri,
  getSilentCheckSsoRedirectUri,
} from './keycloakRedirect';

export const keycloak = new Keycloak({
  url: process.env.VUE_APP_KEYCLOAK_URL,
  realm: process.env.VUE_APP_KEYCLOAK_REALM,
  clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
});

export async function KeycloakAuthInit(options) {
  const { store, router, authComplete } = options;
  let isImpersonationAuthenticated = false;
  let authInitComplete = false;

  const consumeImpersonationTicketFromUrl = async () => {
    const currentUrl = new URL(window.location.href);
    const ticket = currentUrl.searchParams.get('impersonationTicket');
    if (!ticket) {
      return false;
    }

    await store.dispatch('auth/consumeImpersonationTicket', ticket);

    currentUrl.searchParams.delete('impersonationTicket');
    const cleanUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState({}, document.title, cleanUrl);

    return true;
  };

  keycloak.onAuthSuccess = async () => {
    await store.dispatch('auth/setKeycloakTokens', {
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
    });
    await store.dispatch('auth/setAuthenticated', true);
    await store.dispatch('auth/setUser');
    await fetchMemberOrganizations();

    setInterval(() => keycloak.updateToken(30), 100000);

    try {
      store.dispatch('auth/setKeycloakProfile');
      store.dispatch('auth/monitorAuthenticated');
    } catch (e) {
      console.error('Keycloak initial error', e);
    }
  };

  keycloak.onAuthLogout = async () => {
    await store.dispatch('auth/resetUser');
    await store.dispatch('auth/setKeycloakTokens', {});
    await fetchMemberOrganizations();

    if (authInitComplete) {
      router.replace({
        name: 'Home',
      });
    }
  };

  keycloak.onAuthRefreshSuccess = async () => {
    await store.dispatch('auth/setKeycloakTokens', {
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
    });
  };

  store.dispatch('auth/isAuthenticating', true);

  try {
    isImpersonationAuthenticated = await consumeImpersonationTicketFromUrl();

    if (!isImpersonationAuthenticated) {
      await keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: getSilentCheckSsoRedirectUri(),
        pkceMethod: 'S256',
        redirectUri: getKeycloakRedirectUri(),
        enableLogging: true,
        checkLoginIframe: false,
        // KC26: nonce checks disabled for keycloak-js compatibility
        useNonce: false,
      });

      if (keycloak.authenticated) {
        clearKeycloakCallbackHash();
        await store.dispatch('auth/setKeycloakTokens', {
          token: keycloak.token,
          refreshToken: keycloak.refreshToken,
        });
        await store.dispatch('auth/setUser');
      }
    }
  } catch (e) {
    console.error('Authentication init error', e);
    clearKeycloakCallbackHash();
    clearKeycloakCallbackStorage();
  }

  store.dispatch('auth/isAuthenticating', false);
  store.dispatch('auth/setAuthenticated', isImpersonationAuthenticated || keycloak.authenticated);

  authInitComplete = true;
  await fetchMemberOrganizations();
  authComplete();
}
